import {
  ADMIN_PAGE_PRIVILEGES,
  getPageActions,
  pagePermissionKeys,
  type AdminPagePrivilege,
  type PrivilegeActionDef,
} from '../constants/adminPagePrivileges';

export const ROLE_NAME_PATTERN = /^[A-Za-z]+( [A-Za-z]+)*$/;
export const ROLE_SLUG_PATTERN = /^[a-z]+(_[a-z]+)*$/;
export const ROLE_DESCRIPTION_MAX = 200;

export const GRANTABLE_PAGES = ADMIN_PAGE_PRIVILEGES.filter((p) => !p.platformOnly);
const WRITE_SUFFIXES = new Set(['create', 'update', 'delete', 'manage']);

export function hasPerm(perms: string[], key: string): boolean {
  if (perms.includes('*') || perms.includes(key)) return true;
  const parts = key.split('.');
  if (perms.includes(`${parts[0]}.*`)) return true;
  if (parts.length >= 3 && perms.includes(`${parts[0]}.${parts[1]}.*`)) return true;
  if (perms.includes(`${parts[0]}.*.*`)) return true;
  return false;
}

/** The old single "manage" key only counts as legacy when it is not itself one of the page's actions. */
function legacyManageKey(page: AdminPagePrivilege): string | null {
  const key = page.managePermission;
  if (!key || key === page.viewPermission) return null;
  return getPageActions(page).some((a) => a.permission === key) ? null : key;
}

export function pageHasView(perms: string[], page: AdminPagePrivilege): boolean {
  return hasPerm(perms, page.viewPermission);
}

export function pageHasAction(perms: string[], action: PrivilegeActionDef): boolean {
  return hasPerm(perms, action.permission);
}

/**
 * Makes saved permissions editable screen-by-screen: legacy manage keys become the
 * explicit actions they stood for, and every granted action carries its View.
 */
export function normalizeRolePermissions(raw: string[]): string[] {
  if (raw.includes('*')) return ['*'];
  const next = new Set(raw);
  for (const page of ADMIN_PAGE_PRIVILEGES) {
    const legacy = legacyManageKey(page);
    if (legacy && next.has(legacy)) {
      for (const action of getPageActions(page)) next.add(action.permission);
    }
  }
  for (const page of ADMIN_PAGE_PRIVILEGES) {
    const legacy = legacyManageKey(page);
    if (legacy) next.delete(legacy);
  }
  for (const page of ADMIN_PAGE_PRIVILEGES) {
    const perms = Array.from(next);
    if (getPageActions(page).some((a) => pageHasAction(perms, a)) && !pageHasView(perms, page)) {
      next.add(page.viewPermission);
    }
  }
  for (const page of ADMIN_PAGE_PRIVILEGES.filter((p) => p.platformOnly)) {
    for (const key of pagePermissionKeys(page)) next.delete(key);
  }
  return Array.from(next).sort();
}

const isWildcard = (key: string) => key !== '*' && key.endsWith('*');
const wildcardCovers = (wildcard: string, key: string) => key.startsWith(wildcard.replace(/\*(\.\*)?$/, ''));

const ALL_CATALOG_KEYS = Array.from(new Set(GRANTABLE_PAGES.flatMap(pagePermissionKeys)));

/** Replaces module wildcards touching these pages with the individual catalog keys they granted. */
export function expandWildcardsFor(perms: string[], pages: AdminPagePrivilege[]): { perms: string[]; expanded: string[] } {
  const pageKeys = pages.flatMap(pagePermissionKeys);
  const expanded = perms.filter((p) => isWildcard(p) && pageKeys.some((k) => wildcardCovers(p, k)));
  if (!expanded.length) return { perms, expanded };
  const next = new Set(perms.filter((p) => !expanded.includes(p)));
  for (const wildcard of expanded) {
    for (const key of ALL_CATALOG_KEYS) {
      if (wildcardCovers(wildcard, key) && !key.endsWith('.manage')) next.add(key);
    }
  }
  return { perms: Array.from(next), expanded };
}

export function allGrantablePermissions(): string[] {
  const keys = new Set<string>();
  for (const page of GRANTABLE_PAGES) {
    keys.add(page.viewPermission);
    for (const action of getPageActions(page)) keys.add(action.permission);
  }
  return Array.from(keys).sort();
}

export function countChanges(a: string[], b: string[]): number {
  const sa = new Set(a);
  const sb = new Set(b);
  let n = 0;
  for (const k of sa) if (!sb.has(k)) n += 1;
  for (const k of sb) if (!sa.has(k)) n += 1;
  return n;
}

export interface RoleCoverage {
  totalScreens: number;
  viewScreens: number;
  writeScreens: number;
  totalActions: number;
  grantedActions: number;
  full: boolean;
  coverage: number;
}

/** Counted per screen: View plus each write action that screen supports (platform-only screens excluded). */
export function roleCoverage(perms: string[]): RoleCoverage {
  const full = perms.includes('*');
  let viewScreens = 0;
  let writeScreens = 0;
  let totalActions = 0;
  let grantedActions = 0;
  for (const page of GRANTABLE_PAGES) {
    const actions = getPageActions(page);
    totalActions += 1 + actions.length;
    const view = full || pageHasView(perms, page);
    if (view) { viewScreens += 1; grantedActions += 1; }
    const grantedWrites = view ? actions.filter((a) => full || pageHasAction(perms, a)).length : 0;
    grantedActions += grantedWrites;
    if (grantedWrites) writeScreens += 1;
  }
  return {
    totalScreens: GRANTABLE_PAGES.length,
    viewScreens,
    writeScreens,
    totalActions,
    grantedActions,
    full,
    coverage: full ? 100 : Math.round((grantedActions / Math.max(totalActions, 1)) * 100),
  };
}

/** Other screens that use the same permission keys, so toggling one changes them too. */
export function sharedScreens(page: AdminPagePrivilege): AdminPagePrivilege[] {
  const keys = new Set(pagePermissionKeys(page));
  return GRANTABLE_PAGES.filter((p) => p.id !== page.id && pagePermissionKeys(p).some((k) => keys.has(k)));
}

export function isWriteKey(key: string): boolean {
  return WRITE_SUFFIXES.has(key.split('.').pop() || '');
}

export function sanitizeRoleName(raw: string): string {
  return raw.replace(/[^A-Za-z ]+/g, '').replace(/\s{2,}/g, ' ').replace(/^\s+/, '').slice(0, 50);
}

export function slugFromRoleName(name: string): string {
  return name.toLowerCase().replace(/[^a-z]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
}

export function sanitizeRoleSlug(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z_]+/g, '').replace(/_{2,}/g, '_').replace(/^_+/, '').slice(0, 40);
}

export function validateRoleName(name: string): string | null {
  const v = name.trim();
  if (!v) return 'Role name is required';
  if (v.length < 3) return 'Role name must be at least 3 characters';
  if (!ROLE_NAME_PATTERN.test(v)) return 'Letters and single spaces only — no numbers or special characters';
  return null;
}

export function validateRoleSlug(slug: string): string | null {
  if (!slug) return 'Slug is required';
  if (slug.length < 3) return 'Slug must be at least 3 characters';
  if (!ROLE_SLUG_PATTERN.test(slug)) return 'Lowercase letters with single underscores between words (e.g. city_supervisor)';
  return null;
}
